import React from "react";
import {
  FiArrowDown,
  FiArrowUp,
  FiChevronDown,
  FiChevronsLeft,
  FiChevronsRight,
  FiChevronUp,
  FiClock,
  FiDownload,
  FiEdit2,
  FiExternalLink,
  FiFileText,
  FiLogOut,
  FiMail,
  FiMoreVertical,
  FiPause,
  FiPlay,
  FiPlusCircle,
  FiRefreshCw,
  FiRepeat,
  FiSquare,
  FiThumbsDown,
  FiThumbsUp,
  FiUpload,
} from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
/*
 * The rich-text formatting verbs. They come from a different family than the
 * rest of the set because the one above draws no ordered list and no heading
 * marks — and a toolbar that borrowed most of its row from one family and
 * hand-drew the gaps would show the seam, since those are the glyphs sitting
 * side by side. This family is a direct descendant of the other: same 24px box,
 * same 2px round-capped stroke, so the two mix without an optical-size pass.
 * `sparkles` comes from here for the plainer reason that the family above
 * draws none.
 */
import {
  LuBold,
  LuCornerDownLeft,
  LuHeading1,
  LuHeading2,
  LuHeading3,
  LuItalic,
  LuLink,
  LuList,
  LuListOrdered,
  LuSparkles,
  LuUnlink,
} from "react-icons/lu";
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import { Icon as SharedIcon } from "@/components";
import type { IconName } from "@/components/icon";
import { cssVariables } from "@/components/css_variables";

/**
 * The Dashboard icon vocabulary. The shared `IconName` union
 * (src/components/icon.tsx) holds only glyphs the white-label offering
 * surface renders; the dashboard-chrome verbs below (download, kebab,
 * eye-toggles, finance glyphs, …) live here so they never join the WL-facing
 * API surface or its bundle. FM code renders through THIS `Icon`; it draws
 * FM-only glyphs locally and delegates shared names to the shared `Icon`.
 *
 * Promotion rule: a glyph is shared iff ≥1 WL call site renders it — promote
 * by moving the name (and renderer) across files in the same PR that adds
 * that WL call site.
 */
export type FmOnlyIconName =
  /*
   * The dashboard's disclosure pair — expand/collapse rows, accordions, and
   * menu triggers. Named for the role rather than the direction alone so the
   * two read as one control, and kept FM-side so the pair can be sized and
   * shaped together: the shared set's own down-caret is load-bearing across the
   * white-label pickers, where a change to it is a customer-facing change.
   */
  | "caret-up"
  | "caret-down"
  /*
   * Reordering a short list in place. Distinct from the carets above, which are
   * the disclosure pair: a caret opens something, an arrow moves it. Sharing one
   * glyph between the two would make a control that reorders look like one that
   * expands.
   */
  | "arrow-up"
  | "arrow-down"
  | "pencil"
  | "download"
  | "clock"
  | "external-link"
  | "chevrons-left"
  | "chevrons-right"
  | "plus-circle"
  | "logout"
  | "grip"
  | "three-dots"
  | "file-text"
  | "eye-outline"
  | "eye-off-outline"
  | "play"
  | "pause"
  | "repeat"
  | "calculator"
  | "cash"
  | "receipt"
  | "upload"
  | "mail"
  | "swap"
  | "google"
  /*
   * The rich-text toolbar's formatting verbs. Dashboard-only by construction:
   * the white-label surface renders authored content, it never authors it, so
   * an editor control has no call site there.
   *
   * The heading marks are typographic — a letter H carrying its level — rather
   * than pictograms, because no drawing means "second-level heading". That is
   * the convention every writing tool shares, so it reads as a glyph and not as
   * a label that failed to become one.
   */
  | "heading-1"
  | "heading-2"
  | "heading-3"
  | "bold"
  | "italic"
  | "bulleted-list"
  | "numbered-list"
  | "link"
  | "unlink"
  | "line-break"
  /*
   * The Eve assistant panel's glyphs. The assistant exists only in the
   * dashboard, so no white-label page renders them.
   *
   * `send` and `stop` are named for the composer's action rather than the
   * drawing (an up arrow, a square), so neither is picked up for "move up" or
   * "sort ascending" by a name that only describes its shape. `send` is a chat
   * message going into the thread; sending an email is still `mail`.
   */
  | "sparkles"
  | "thumbs-up"
  | "thumbs-down"
  | "send"
  | "stop";

export type FmIconName = IconName | FmOnlyIconName;

// Every dashboard-only name, as a Record so tsc fails when the union gains a
// name this map lacks; the runtime set is derived from it and cannot drift.
export const FM_ONLY_ICON_NAMES: Readonly<Record<FmOnlyIconName, true>> = {
  "caret-up": true,
  "caret-down": true,
  "arrow-up": true,
  "arrow-down": true,
  pencil: true,
  download: true,
  clock: true,
  "external-link": true,
  "chevrons-left": true,
  "chevrons-right": true,
  "plus-circle": true,
  logout: true,
  grip: true,
  "three-dots": true,
  "file-text": true,
  "eye-outline": true,
  "eye-off-outline": true,
  play: true,
  pause: true,
  repeat: true,
  calculator: true,
  cash: true,
  receipt: true,
  upload: true,
  mail: true,
  swap: true,
  google: true,
  "heading-1": true,
  "heading-2": true,
  "heading-3": true,
  bold: true,
  italic: true,
  "bulleted-list": true,
  "numbered-list": true,
  link: true,
  unlink: true,
  "line-break": true,
  sparkles: true,
  "thumbs-up": true,
  "thumbs-down": true,
  send: true,
  stop: true,
};

const FM_ONLY: ReadonlySet<string> = new Set(Object.keys(FM_ONLY_ICON_NAMES));

// Type predicate so the dispatcher narrows without `as` casts: the false
// branch is exactly the shared union, the true branch exactly the FM one.
function isFmOnlyIcon(name: FmIconName): name is FmOnlyIconName {
  return FM_ONLY.has(name);
}

interface FmIconProps {
  name: FmIconName;
  color?: string;
  className?: string;
  size?: number;
}

export function Icon(props: FmIconProps) {
  const {
    name,
    color = cssVariables.textColorDefault,
    className,
    size = 24,
  } = props;

  if (!isFmOnlyIcon(name)) {
    return (
      <SharedIcon name={name} color={color} className={className} size={size} />
    );
  }

  switch (name) {
    /*
     * Both halves render from the same family at the same weight, so the glyph
     * only ever changes direction as a row opens and closes — never size. The
     * pair this replaced hardcoded a 1em box (ignoring `size`) around a hairline
     * path in a far larger viewBox, which left a caret of a few pixels.
     */
    case "caret-up":
      return <FiChevronUp color={color} className={className} size={size} />;
    case "caret-down":
      return <FiChevronDown color={color} className={className} size={size} />;
    case "arrow-up":
      return <FiArrowUp color={color} className={className} size={size} />;
    case "arrow-down":
      return <FiArrowDown color={color} className={className} size={size} />;
    /*
     * Stroked, from the same family as the rest of the dashboard set. Fill is
     * reserved here for status indicators and brand marks: an action verb drawn
     * as a solid silhouette out-weighs the outlines it stacks against in a menu
     * column and emphasizes itself without anyone choosing that. The white-label
     * surface keeps its own filled pencil under a separate name, where the
     * heavier weight IS the intent.
     */
    case "pencil":
      return <FiEdit2 color={color} className={className} size={size} />;
    case "download":
      return (
        <FiDownload
          strokeWidth={1}
          color={color}
          className={className}
          size={size}
        />
      );
    case "clock":
      return <FiClock color={color} className={className} size={size} />;
    case "external-link":
      return <FiExternalLink color={color} className={className} size={size} />;
    case "chevrons-left":
      return <FiChevronsLeft color={color} className={className} size={size} />;
    case "chevrons-right":
      return (
        <FiChevronsRight color={color} className={className} size={size} />
      );
    case "plus-circle":
      return <FiPlusCircle color={color} className={className} size={size} />;
    case "logout":
      return <FiLogOut color={color} className={className} size={size} />;
    case "grip":
      return <Grip color={color} className={className} size={size} />;
    case "three-dots":
      return <FiMoreVertical color={color} className={className} size={size} />;
    case "file-text":
      return <FiFileText color={color} className={className} size={size} />;
    case "eye-outline":
      return <IoEyeOutline color={color} className={className} size={size} />;
    case "eye-off-outline":
      return (
        <IoEyeOffOutline color={color} className={className} size={size} />
      );
    case "play":
      return <FiPlay color={color} className={className} size={size} />;
    case "repeat":
      return <FiRefreshCw color={color} className={className} size={size} />;
    case "pause":
      return <FiPause color={color} className={className} size={size} />;
    case "calculator":
      return <CalculatorIcon color={color} className={className} size={size} />;
    case "cash":
      return <CashIcon color={color} className={className} size={size} />;
    case "receipt":
      return <ReceiptIcon color={color} className={className} size={size} />;
    case "upload":
      return <FiUpload color={color} className={className} size={size} />;
    case "swap":
      return <FiRepeat color={color} className={className} size={size} />;
    case "mail":
      return (
        <FiMail
          strokeWidth={1}
          color={color}
          className={className}
          size={size}
        />
      );
    /*
     * A brand mark, not an action verb — the one kind of glyph this file's
     * fill rule reserves fill for. It draws in Google's own colors and
     * deliberately ignores `color`: a recolored G is a different logo.
     */
    case "google":
      return <FcGoogle className={className} size={size} />;
    case "heading-1":
      return <LuHeading1 color={color} className={className} size={size} />;
    case "heading-2":
      return <LuHeading2 color={color} className={className} size={size} />;
    case "heading-3":
      return <LuHeading3 color={color} className={className} size={size} />;
    case "bold":
      return <LuBold color={color} className={className} size={size} />;
    case "italic":
      return <LuItalic color={color} className={className} size={size} />;
    case "bulleted-list":
      return <LuList color={color} className={className} size={size} />;
    case "numbered-list":
      return <LuListOrdered color={color} className={className} size={size} />;
    case "link":
      return <LuLink color={color} className={className} size={size} />;
    case "unlink":
      return <LuUnlink color={color} className={className} size={size} />;
    /*
     * A return arrow, not a ¶ or a ⏎ key cap: the control inserts the break
     * that shift+enter would, so the glyph shows where the text goes rather
     * than naming the key or the mark left behind.
     */
    case "line-break":
      return (
        <LuCornerDownLeft color={color} className={className} size={size} />
      );
    case "sparkles":
      return <LuSparkles color={color} className={className} size={size} />;
    case "thumbs-up":
      return <FiThumbsUp color={color} className={className} size={size} />;
    case "thumbs-down":
      return <FiThumbsDown color={color} className={className} size={size} />;
    case "send":
      return <FiArrowUp color={color} className={className} size={size} />;
    /*
     * An outlined square, not the solid block media players draw: `stop` is an
     * action verb, and the fill rule above keeps verbs stroked.
     */
    case "stop":
      return <FiSquare color={color} className={className} size={size} />;
  }
}

interface GlyphProps {
  color?: string;
  className?: string;
  size?: number;
}

function Grip({ className, color, size }: GlyphProps) {
  return (
    <svg
      className={className}
      width={size || "1em"}
      height={size || "1em"}
      viewBox="0 0 16 16"
      fill={color}
    >
      <circle cx="6" cy="4" r="1.1" />
      <circle cx="10" cy="4" r="1.1" />
      <circle cx="6" cy="8" r="1.1" />
      <circle cx="10" cy="8" r="1.1" />
      <circle cx="6" cy="12" r="1.1" />
      <circle cx="10" cy="12" r="1.1" />
    </svg>
  );
}

function CalculatorIcon({ className, color, size }: GlyphProps) {
  return (
    <svg
      className={className}
      width={size || 14}
      height={size || 14}
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.5267 0L2.00675 0C1.78397 0 1.57031 0.0884997 1.41278 0.24603C1.25525 0.403561 1.16675 0.617218 1.16675 0.84L1.16675 13.16C1.16675 13.3828 1.25525 13.5964 1.41278 13.754C1.57031 13.9115 1.78397 14 2.00675 14L11.5267 14C11.7495 14 11.9632 13.9115 12.1207 13.754C12.2782 13.5964 12.3667 13.3828 12.3667 13.16L12.3667 0.84C12.3667 0.617218 12.2782 0.403561 12.1207 0.24603C11.9632 0.0884997 11.7495 0 11.5267 0V0ZM3.96675 11.76C3.85599 11.76 3.74772 11.7272 3.65563 11.6656C3.56354 11.6041 3.49176 11.5166 3.44938 11.4143C3.40699 11.312 3.3959 11.1994 3.41751 11.0907C3.43912 10.9821 3.49245 10.8823 3.57077 10.804C3.64909 10.7257 3.74887 10.6724 3.8575 10.6508C3.96613 10.6292 4.07872 10.6402 4.18105 10.6826C4.28338 10.725 4.37084 10.7968 4.43237 10.8889C4.4939 10.981 4.52675 11.0892 4.52675 11.2C4.52675 11.3485 4.46775 11.491 4.36273 11.596C4.25771 11.701 4.11527 11.76 3.96675 11.76ZM3.96675 8.96C3.85599 8.96 3.74772 8.92716 3.65563 8.86562C3.56354 8.80409 3.49176 8.71663 3.44938 8.6143C3.40699 8.51198 3.3959 8.39938 3.41751 8.29075C3.43912 8.18212 3.49245 8.08234 3.57077 8.00402C3.64909 7.9257 3.74887 7.87237 3.8575 7.85076C3.96613 7.82915 4.07872 7.84024 4.18105 7.88263C4.28338 7.92501 4.37084 7.99679 4.43237 8.08888C4.4939 8.18097 4.52675 8.28924 4.52675 8.4C4.52675 8.54852 4.46775 8.69096 4.36273 8.79598C4.25771 8.901 4.11527 8.96 3.96675 8.96ZM3.96675 6.16C3.85599 6.16 3.74772 6.12716 3.65563 6.06562C3.56354 6.00409 3.49176 5.91663 3.44938 5.8143C3.40699 5.71198 3.3959 5.59938 3.41751 5.49075C3.43912 5.38212 3.49245 5.28234 3.57077 5.20402C3.64909 5.1257 3.74887 5.07237 3.8575 5.05076C3.96613 5.02915 4.07872 5.04024 4.18105 5.08263C4.28338 5.12501 4.37084 5.19679 4.43237 5.28888C4.4939 5.38097 4.52675 5.48924 4.52675 5.6C4.52675 5.74852 4.46775 5.89096 4.36273 5.99598C4.25771 6.101 4.11527 6.16 3.96675 6.16ZM6.76675 11.76C6.65599 11.76 6.54772 11.7272 6.45563 11.6656C6.36354 11.6041 6.29176 11.5166 6.24938 11.4143C6.20699 11.312 6.1959 11.1994 6.21751 11.0907C6.23912 10.9821 6.29245 10.8823 6.37077 10.804C6.44909 10.7257 6.54887 10.6724 6.6575 10.6508C6.76613 10.6292 6.87873 10.6402 6.98105 10.6826C7.08338 10.725 7.17084 10.7968 7.23237 10.8889C7.29391 10.981 7.32675 11.0892 7.32675 11.2C7.32675 11.3485 7.26775 11.491 7.16273 11.596C7.05771 11.701 6.91527 11.76 6.76675 11.76ZM6.76675 8.96C6.65599 8.96 6.54772 8.92716 6.45563 8.86562C6.36354 8.80409 6.29176 8.71663 6.24938 8.6143C6.20699 8.51198 6.1959 8.39938 6.21751 8.29075C6.23912 8.18212 6.29245 8.08234 6.37077 8.00402C6.44909 7.9257 6.54887 7.87237 6.6575 7.85076C6.76613 7.82915 6.87873 7.84024 6.98105 7.88263C7.08338 7.92501 7.17084 7.99679 7.23237 8.08888C7.29391 8.18097 7.32675 8.28924 7.32675 8.4C7.32675 8.54852 7.26775 8.69096 7.16273 8.79598C7.05771 8.901 6.91527 8.96 6.76675 8.96ZM6.76675 6.16C6.65599 6.16 6.54772 6.12716 6.45563 6.06562C6.36354 6.00409 6.29176 5.91663 6.24938 5.8143C6.20699 5.71198 6.1959 5.59938 6.21751 5.49075C6.23912 5.38212 6.29245 5.28234 6.37077 5.20402C6.44909 5.1257 6.54887 5.07237 6.6575 5.05076C6.76613 5.02915 6.87873 5.04024 6.98105 5.08263C7.08338 5.12501 7.17084 5.19679 7.23237 5.28888C7.29391 5.38097 7.32675 5.48924 7.32675 5.6C7.32675 5.74852 7.26775 5.89096 7.16273 5.99598C7.05771 6.101 6.91527 6.16 6.76675 6.16ZM9.56675 11.76C9.45599 11.76 9.34772 11.7272 9.25563 11.6656C9.16354 11.6041 9.09176 11.5166 9.04938 11.4143C9.00699 11.312 8.9959 11.1994 9.01751 11.0907C9.03912 10.9821 9.09245 10.8823 9.17077 10.804C9.24909 10.7257 9.34887 10.6724 9.4575 10.6508C9.56613 10.6292 9.67872 10.6402 9.78105 10.6826C9.88338 10.725 9.97084 10.7968 10.0324 10.8889C10.0939 10.981 10.1267 11.0892 10.1267 11.2C10.1267 11.3485 10.0677 11.491 9.96273 11.596C9.85771 11.701 9.71527 11.76 9.56675 11.76ZM9.56675 8.96C9.45599 8.96 9.34772 8.92716 9.25563 8.86562C9.16354 8.80409 9.09176 8.71663 9.04938 8.6143C9.00699 8.51198 8.9959 8.39938 9.01751 8.29075C9.03912 8.18212 9.09245 8.08234 9.17077 8.00402C9.24909 7.9257 9.34887 7.87237 9.4575 7.85076C9.56613 7.82915 9.67872 7.84024 9.78105 7.88263C9.88338 7.92501 9.97084 7.99679 10.0324 8.08888C10.0939 8.18097 10.1267 8.28924 10.1267 8.4C10.1267 8.54852 10.0677 8.69096 9.96273 8.79598C9.85771 8.901 9.71527 8.96 9.56675 8.96ZM9.56675 6.16C9.45599 6.16 9.34772 6.12716 9.25563 6.06562C9.16354 6.00409 9.09176 5.91663 9.04938 5.8143C9.00699 5.71198 8.9959 5.59938 9.01751 5.49075C9.03912 5.38212 9.09245 5.28234 9.17077 5.20402C9.24909 5.1257 9.34887 5.07237 9.4575 5.05076C9.56613 5.02915 9.67872 5.04024 9.78105 5.08263C9.88338 5.12501 9.97084 5.19679 10.0324 5.28888C10.0939 5.38097 10.1267 5.48924 10.1267 5.6C10.1267 5.74852 10.0677 5.89096 9.96273 5.99598C9.85771 6.101 9.71527 6.16 9.56675 6.16ZM10.4067 2.8L3.12675 2.8C3.05249 2.8 2.98127 2.7705 2.92876 2.71799C2.87625 2.66548 2.84675 2.59426 2.84675 2.52C2.84675 2.44574 2.87625 2.37452 2.92876 2.32201C2.98127 2.2695 3.05249 2.24 3.12675 2.24L10.4067 2.24C10.481 2.24 10.5522 2.2695 10.6047 2.32201C10.6572 2.37452 10.6867 2.44574 10.6867 2.52C10.6867 2.59426 10.6572 2.66548 10.6047 2.71799C10.5522 2.7705 10.481 2.8 10.4067 2.8Z"
        fill={color || cssVariables.textColorPrimary}
      />
    </svg>
  );
}

function CashIcon({ className, color, size }: GlyphProps) {
  return (
    <svg
      className={className}
      width={size || 14}
      height={size || 14}
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.88 1.75L1.4 1.75C1.32573 1.75 1.25452 1.7795 1.20201 1.83201C1.1495 1.88452 1.12 1.95574 1.12 2.03C1.12 2.10426 1.1495 2.17548 1.20201 2.22799C1.25452 2.2805 1.32573 2.31 1.4 2.31L12.88 2.31C13.0285 2.31015 13.1708 2.3692 13.2758 2.47418C13.3808 2.57917 13.4398 2.72152 13.44 2.87L13.44 10.43C13.44 10.5043 13.4695 10.5755 13.522 10.628C13.5745 10.6805 13.6457 10.71 13.72 10.71C13.7943 10.71 13.8655 10.6805 13.918 10.628C13.9705 10.5755 14 10.5043 14 10.43L14 2.87C13.9997 2.57306 13.8815 2.28838 13.6716 2.07842C13.4616 1.86845 13.1769 1.75034 12.88 1.75V1.75Z"
        fill={color || cssVariables.textColorPrimary}
      />
      <path
        d="M12.32 2.87012L0.56 2.87012C0.411479 2.87012 0.269041 2.92912 0.16402 3.03414C0.0589998 3.13916 0 3.2816 0 3.43012L0 11.2701C0 11.4186 0.0589998 11.5611 0.16402 11.6661C0.269041 11.7711 0.411479 11.8301 0.56 11.8301L12.32 11.8301C12.4685 11.8301 12.611 11.7711 12.716 11.6661C12.821 11.5611 12.88 11.4186 12.88 11.2701V3.43012C12.88 3.2816 12.821 3.13916 12.716 3.03414C12.611 2.92912 12.4685 2.87012 12.32 2.87012ZM2.52 5.11012H1.4C1.32574 5.11012 1.25452 5.08062 1.20201 5.02811C1.1495 4.9756 1.12 4.90438 1.12 4.83012C1.12 4.75586 1.1495 4.68464 1.20201 4.63213C1.25452 4.57962 1.32574 4.55012 1.4 4.55012H2.52C2.59426 4.55012 2.66548 4.57962 2.71799 4.63213C2.7705 4.68464 2.8 4.75586 2.8 4.83012C2.8 4.90438 2.7705 4.9756 2.71799 5.02811C2.66548 5.08062 2.59426 5.11012 2.52 5.11012ZM6.44 9.59012C5.88398 9.55056 5.36608 9.293 4.999 8.87349C4.63193 8.45398 4.4454 7.90647 4.48 7.35012C4.44539 6.79376 4.63191 6.24624 4.99899 5.82673C5.36606 5.40722 5.88397 5.14966 6.44 5.11012C6.99603 5.14966 7.51394 5.40722 7.88101 5.82673C8.24809 6.24624 8.43461 6.79376 8.4 7.35012C8.4346 7.90647 8.24807 8.45398 7.881 8.87349C7.51392 9.293 6.99603 9.55056 6.44 9.59012ZM11.48 10.1501H10.36C10.2857 10.1501 10.2145 10.1206 10.162 10.0681C10.1095 10.0156 10.08 9.94438 10.08 9.87012C10.08 9.79586 10.1095 9.72464 10.162 9.67213C10.2145 9.61962 10.2857 9.59012 10.36 9.59012H11.48C11.5543 9.59012 11.6255 9.61962 11.678 9.67213C11.7305 9.72464 11.76 9.79586 11.76 9.87012C11.76 9.94438 11.7305 10.0156 11.678 10.0681C11.6255 10.1206 11.5543 10.1501 11.48 10.1501Z"
        fill={color || cssVariables.textColorPrimary}
      />
    </svg>
  );
}

function ReceiptIcon({ className, color, size }: GlyphProps) {
  return (
    <svg
      className={className}
      width={size || 14}
      height={size || 14}
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.5501 0L2.59006 0C2.36734 0.00020758 2.15381 0.088774 1.99632 0.24626C1.83884 0.403745 1.75027 0.617282 1.75006 0.84L1.75006 13.72C1.75007 13.7707 1.76382 13.8204 1.78987 13.8639C1.81591 13.9073 1.85326 13.9429 1.89794 13.9668C1.94262 13.9907 1.99295 14.0021 2.04356 13.9996C2.09418 13.9972 2.14318 13.9811 2.18535 13.953L3.71006 12.9364L5.23477 13.9528C5.28073 13.9835 5.33479 14 5.39009 14C5.44539 14 5.49945 13.9835 5.5454 13.9528L7.07006 12.9364L8.59477 13.9528C8.64073 13.9835 8.69479 14 8.75009 14C8.80539 14 8.85944 13.9835 8.9054 13.9528L10.4301 12.9364L11.9548 13.9528C11.9969 13.9809 12.0459 13.997 12.0965 13.9995C12.1471 14.0019 12.1974 13.9906 12.2421 13.9667C12.2868 13.9428 12.3241 13.9073 12.3502 13.8638C12.3762 13.8204 12.39 13.7707 12.3901 13.72L12.3901 0.84C12.3899 0.617282 12.3013 0.403745 12.1438 0.24626C11.9863 0.088774 11.7728 0.00020758 11.5501 0V0ZM8.75006 10.64H5.39006C5.3158 10.64 5.24458 10.6105 5.19207 10.558C5.13956 10.5055 5.11006 10.4343 5.11006 10.36C5.11006 10.2857 5.13956 10.2145 5.19207 10.162C5.24458 10.1095 5.3158 10.08 5.39006 10.08H8.75006C8.82432 10.08 8.89554 10.1095 8.94805 10.162C9.00056 10.2145 9.03006 10.2857 9.03006 10.36C9.03006 10.4343 9.00056 10.5055 8.94805 10.558C8.89554 10.6105 8.82432 10.64 8.75006 10.64ZM8.75006 8.96H5.39006C5.3158 8.96 5.24458 8.9305 5.19207 8.87799C5.13956 8.82548 5.11006 8.75426 5.11006 8.68C5.11006 8.60574 5.13956 8.53452 5.19207 8.48201C5.24458 8.4295 5.3158 8.4 5.39006 8.4H8.75006C8.82432 8.4 8.89554 8.4295 8.94805 8.48201C9.00056 8.53452 9.03006 8.60574 9.03006 8.68C9.03006 8.75426 9.00056 8.82548 8.94805 8.87799C8.89554 8.9305 8.82432 8.96 8.75006 8.96ZM8.75006 3.36L5.95006 3.36C5.87583 3.3601 5.80467 3.38964 5.75219 3.44212C5.6997 3.49461 5.67016 3.56577 5.67006 3.64V4.2C5.67016 4.27423 5.6997 4.34539 5.75219 4.39788C5.80467 4.45036 5.87583 4.4799 5.95006 4.48L8.19006 4.48C8.41277 4.48024 8.62629 4.56881 8.78377 4.72629C8.94125 4.88377 9.02982 5.09729 9.03006 5.32V5.88C9.02982 6.10271 8.94125 6.31623 8.78377 6.47371C8.62629 6.63119 8.41277 6.71976 8.19006 6.72H7.35006V7C7.35006 7.07426 7.32056 7.14548 7.26805 7.19799C7.21554 7.2505 7.14432 7.28 7.07006 7.28C6.9958 7.28 6.92458 7.2505 6.87207 7.19799C6.81956 7.14548 6.79006 7.07426 6.79006 7V6.72L5.39006 6.72C5.3158 6.72 5.24458 6.6905 5.19207 6.63799C5.13956 6.58548 5.11006 6.51426 5.11006 6.44C5.11006 6.36574 5.13956 6.29452 5.19207 6.24201C5.24458 6.1895 5.3158 6.16 5.39006 6.16L8.19006 6.16C8.26429 6.1599 8.33545 6.13036 8.38794 6.07788C8.44042 6.02539 8.46996 5.95423 8.47006 5.88V5.32C8.46996 5.24577 8.44042 5.17461 8.38794 5.12212C8.33545 5.06964 8.26429 5.0401 8.19006 5.04L5.95006 5.04C5.72735 5.03976 5.51383 4.95119 5.35635 4.79371C5.19887 4.63623 5.1103 4.42271 5.11006 4.2V3.64C5.1103 3.41729 5.19887 3.20377 5.35635 3.04629C5.51383 2.88881 5.72735 2.80024 5.95006 2.8H6.79006V2.52C6.79006 2.44574 6.81956 2.37452 6.87207 2.32201C6.92458 2.2695 6.9958 2.24 7.07006 2.24C7.14432 2.24 7.21554 2.2695 7.26805 2.32201C7.32056 2.37452 7.35006 2.44574 7.35006 2.52V2.8L8.75006 2.8C8.82432 2.8 8.89554 2.8295 8.94805 2.88201C9.00056 2.93452 9.03006 3.00574 9.03006 3.08C9.03006 3.15426 9.00056 3.22548 8.94805 3.27799C8.89554 3.3305 8.82432 3.36 8.75006 3.36Z"
        fill={color || cssVariables.textColorPrimary}
      />
    </svg>
  );
}
